import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MoodIcon from '@mui/icons-material/Mood';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { fetchMySchedules } from '../../api/schedules';
import { fetchTutorStudents } from '../../api/students';
import { fetchTutorProfile, updateDailyMood } from '../../api/tutorProfile';
import { getWeekStartDate } from '../../utils/week';

const B = { primary: '#4357AD', green: '#D3FEC6', orange: '#FF521B', dark: '#1E2A4A' };

const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

const insightStyles = {
  warning: { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E', icon: <WarningAmberIcon fontSize="small" /> },
  info:    { bg: '#EEF2FF', border: '#C7D2FE', color: '#3730A3', icon: <InfoOutlinedIcon fontSize="small" /> },
  success: { bg: '#F0FDF4', border: '#BBF7D0', color: '#166534', icon: <TipsAndUpdatesIcon fontSize="small" /> },
};

const TutorHomePage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const today = dayjs();
  const weekStart = getWeekStartDate(today).format('YYYY-MM-DD');
  const prevWeekStart = getWeekStartDate(today.subtract(1, 'week')).format('YYYY-MM-DD');

  const queryClient = useQueryClient();
  const scheduleQuery = useQuery({ queryKey: ['schedule-me', weekStart], queryFn: () => fetchMySchedules(weekStart), enabled: Boolean(user) });
  const prevScheduleQuery = useQuery({ queryKey: ['schedule-me', prevWeekStart], queryFn: () => fetchMySchedules(prevWeekStart), enabled: Boolean(user) });
  const studentsQuery = useQuery({ queryKey: ['tutor-students', user?.id], queryFn: () => fetchTutorStudents(user.id), enabled: Boolean(user?.id) });
  const profileQuery = useQuery({ queryKey: ['tutor-profile', user?.id], queryFn: () => fetchTutorProfile(user.id), enabled: Boolean(user?.id) });

  const [moodInput, setMoodInput] = useState('');
  const [moodEditing, setMoodEditing] = useState(false);
  // Random seed that changes on every mount (page visit)
  const [insightSeed] = useState(() => Math.random());

  // Dismissed insights — stored in localStorage, expire at end of day
  const [dismissedInsights, setDismissedInsights] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('dismissedInsights') || '{}');
      const todayKey = dayjs().format('YYYY-MM-DD');
      if (stored.date !== todayKey) return { date: todayKey, texts: [] };
      return stored;
    } catch { return { date: dayjs().format('YYYY-MM-DD'), texts: [] }; }
  });

  const dismissInsight = (text) => {
    setDismissedInsights((prev) => {
      const next = { ...prev, texts: [...prev.texts, text] };
      localStorage.setItem('dismissedInsights', JSON.stringify(next));
      return next;
    });
  };

  const moodMutation = useMutation({
    mutationFn: (mood) => updateDailyMood(user.id, mood),
    onSuccess: () => {
      enqueueSnackbar('Настрій оновлено!', { variant: 'success' });
      setMoodEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', user?.id] });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити настрій', { variant: 'error' }),
  });

  const currentMood = profileQuery.data?.dailyMood;
  const hasMood = Boolean(currentMood);

  const todayStr = today.format('YYYY-MM-DD');
  const events = scheduleQuery.data || [];
  const prevEvents = prevScheduleQuery.data || [];

  const todayEvents = events.filter((e) => dayjs(e.startTime).format('YYYY-MM-DD') === todayStr);
  const todayTotal = todayEvents.length;
  const todayCompleted = todayEvents.filter((e) => e.status === 'COMPLETED').length;
  const todayRescheduled = todayEvents.filter((e) => e.status === 'RESCHEDULED').length;
  const todayCancelled = todayEvents.filter((e) => e.status === 'CANCELLED').length;

  const weekScheduled = events.filter((e) => e.status === 'SCHEDULED').length;
  const weekCompleted = events.filter((e) => e.status === 'COMPLETED').length;
  const weekCancelled = events.filter((e) => e.status === 'CANCELLED').length;
  const weekRescheduled = events.filter((e) => e.status === 'RESCHEDULED').length;
  const prevCancelled = prevEvents.filter((e) => e.status === 'CANCELLED').length;

  const studentCount = (studentsQuery.data || []).length;
  const incompleteStudents = (studentsQuery.data || []).filter((s) => s.incompleteLessons > 0).length;
  const totalIncomplete = (studentsQuery.data || []).reduce((sum, s) => sum + (s.incompleteLessons || 0), 0);

  // Chart data: per day of the week
  const chartData = useMemo(() => {
    const dayLabels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
    const ws = dayjs(weekStart);
    return dayLabels.map((label, i) => {
      const date = ws.add(i, 'day').format('YYYY-MM-DD');
      const dayEvts = events.filter((e) => dayjs(e.startTime).format('YYYY-MM-DD') === date);
      return {
        day: label,
        Проведено: dayEvts.filter((e) => e.status === 'COMPLETED').length,
        Заплановано: dayEvts.filter((e) => e.status === 'SCHEDULED').length,
        Перенесено: dayEvts.filter((e) => e.status === 'RESCHEDULED').length,
        Скасовано: dayEvts.filter((e) => e.status === 'CANCELLED').length,
      };
    });
  }, [events, weekStart]);

  // Upcoming lessons within 24 hours
  const upcoming = useMemo(() => {
    const now = dayjs();
    const in24h = now.add(24, 'hour');
    return events
      .filter((e) => e.status === 'SCHEDULED' && dayjs(e.startTime).isAfter(now) && dayjs(e.startTime).isBefore(in24h))
      .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf())
      .slice(0, 5);
  }, [events]);

  // Journal reminders: students with incomplete lessons
  const journalReminders = useMemo(() => {
    const students = studentsQuery.data || [];
    return students
      .filter((s) => s.incompleteLessons > 0)
      .map((s) => ({
        studentId: s.id,
        studentName: s.name,
        count: s.incompleteLessons,
      }));
  }, [studentsQuery.data]);

  const allJournalsDone = journalReminders.length === 0;

  const doneMessages = [
    'Молодець! Всі журнали заповнено. Ти найкращий!',
    'Всі журнали актуальні — батьки будуть задоволені!',
    'Бездоганна робота! Жоден журнал не залишився без уваги.',
    'Супер! Все заповнено вчасно. Продовжуй у тому ж дусі!',
    'Ідеальний порядок у журналах! Так тримати!',
    'Всі дані на місці — ти зразковий репетитор!',
    'Журнали в порядку! Тепер можна з чистою совістю відпочити.',
    'Все заповнено! Учні та батьки бачать твою старанність.',
  ];
  const doneMessage = doneMessages[Math.floor(today.valueOf() / 86400000) % doneMessages.length];

  // Insights
  const insights = useMemo(() => {
    const result = [];
    const dayNames = ['неділю', 'понеділок', 'вівторок', 'середу', 'четвер', "п'ятницю", 'суботу'];
    const byDay = {};
    events.forEach((e) => { const d = dayjs(e.startTime).format('YYYY-MM-DD'); if (!byDay[d]) byDay[d] = []; byDay[d].push(e); });

    Object.entries(byDay).forEach(([date, de]) => {
      if (de.length < 2) return;
      const sorted = [...de].sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());
      let c = 1, m = 1;
      for (let i = 1; i < sorted.length; i++) {
        if (dayjs(sorted[i].startTime).diff(dayjs(sorted[i - 1].endTime), 'minute') <= 5) { c++; m = Math.max(m, c); } else c = 1;
      }
      if (m >= 3) result.push({ type: 'warning', text: `Ого, у тебе ${m} уроків поспіль без перерви у ${dayNames[dayjs(date).day()]}. Ти взагалі встигаєш відпочивати?` });
    });
    Object.entries(byDay).forEach(([date, de]) => {
      if (de.length > 5) result.push({ type: 'warning', text: `${de.length} уроків у ${dayNames[dayjs(date).day()]} — не перенапружуйся!` });
    });
    if (weekCancelled > 0) result.push({ type: 'info', text: `${weekCancelled} скасован${weekCancelled === 1 ? 'ий урок' : 'их уроків'} цього тижня. Спробуй перенести.` });
    if (prevCancelled > 0) result.push({ type: 'info', text: `Минулого тижня ${prevCancelled} скасован${prevCancelled === 1 ? 'ий' : 'их'}. Не забувай відпрацьовувати!` });
    if (totalIncomplete > 0) result.push({ type: 'warning', text: `${totalIncomplete} незаповнен${totalIncomplete === 1 ? 'ий урок' : 'их уроків'} в журналах.` });

    if (weekRescheduled > 2) result.push({ type: 'info', text: `Перенесено ${weekRescheduled} занять. Спробуй стабілізувати графік.` });
    if (todayTotal > 0 && todayCompleted === todayTotal) result.push({ type: 'success', text: 'Всі заняття на сьогодні проведено!' });

    // Feature tips — always pick enough to fill 4 slots total
    const featureTips = [
      'Заняття в розкладі можна перетягувати на інший час або день — просто затисни і перенеси!',
      'Скопіюй розклад з минулого тижня одним кліком — шукай іконку копіювання поруч з вибором тижня.',
      'При додаванні заняття в журнал воно автоматично з\'являється в розкладі. Не забудь уточнити час!',
      'Знак оклику в розкладі нагадує про заняття, що потребують твоєї уваги.',
      'Після проведення уроку зміни статус у розкладі на «Проведено» — так статистика буде точною.',
      'Додай коментар до кожного заняття в розкладі — натисни на подію.',
      'Заповнюй оцінки в журналі — батьки бачать прогрес дитини в реальному часі!',
      'Тижневий звіт допомагає відстежити навантаження та настрій — заповнюй щотижня.',
      'Спробуй упорядкувати учнів у зручному для тебе порядку — просто перетягни картки!',
      'Видалити всі заняття тижня можна одним кліком — іконка кошика поруч з копіюванням.',
      'Ти можеш вказати свій настрій на день — натисни на хмаринку під привітанням.',
      'Натисни на свій аватар у бічній панелі — потрапиш у свій особистий профіль.',
      'Спробуй заповнити тижневий звіт на сторінці розкладу — це допомагає аналізувати тренди.',
      'Порада: роби перерви між уроками — це підвищує продуктивність і настрій.',
      'Заповнені журнали — задоволені батьки. Не відкладай на потім!',
      'Фільтр «Незаповнені» на сторінці учнів показує тих, хто потребує уваги.',
      'Сортуй учнів за класом, алфавітом, кількістю занять або незаповненими — спробуй всі варіанти!',
    ];
    const shuffled = [...featureTips].sort(() => insightSeed - 0.5 + Math.random() * 0.01);
    // Fill up to 4 items total — warnings first, then tips
    const tipsNeeded = Math.max(4 - result.length, 1);
    shuffled.slice(0, tipsNeeded).forEach((text) => {
      result.push({ type: 'success', text });
    });

    return result;
  }, [events, prevEvents, weekCancelled, prevCancelled, totalIncomplete, weekRescheduled, todayTotal, todayCompleted, insightSeed]);

  if (scheduleQuery.isLoading || studentsQuery.isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
  }

  const weekRelevant = events.filter((e) => e.status !== 'RESCHEDULED').length;
  const weekProgress = weekRelevant > 0 ? Math.round((weekCompleted / weekRelevant) * 100) : 0;

  return (
    <Stack spacing={2.5}>
      {/* Row 1: Greeting (with mood bubble) + Insights side by side */}
      <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
        <Card
          sx={{
            ...cardSx,
            background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`,
            color: '#fff',
            flex: { md: '0 0 55%' },
            minWidth: 0,
          }}
        >
          <CardContent sx={{ py: 3.5, px: 3.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" sx={{ opacity: 0.6, mb: 0.5 }}>{today.format('dddd, DD MMMM YYYY')}</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1.5 }}>Привіт, {user?.name}!</Typography>
            <Typography variant="h6" fontWeight={400} sx={{ opacity: 0.85, mb: 2.5 }}>
              {todayTotal === 0 ? 'Сьогодні у тебе вільний день — відпочивай!' : `Сьогодні у тебе ${todayTotal} ${todayTotal === 1 ? 'заняття' : 'занять'}`}
            </Typography>

            {/* Mood — inline speech bubble */}
            {!moodEditing && hasMood ? (
              <Box
                onClick={() => { setMoodInput(currentMood); setMoodEditing(true); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: '18px 18px 18px 4px',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
              >
                <MoodIcon sx={{ fontSize: 18, color: B.green, flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={500} sx={{ color: '#fff' }} noWrap>
                  {currentMood}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ alignSelf: 'stretch', width: '100%' }}>
                <TextField
                  size="small"
                  placeholder="Який у тебе настрій сьогодні?"
                  value={moodInput}
                  onChange={(e) => setMoodInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && moodInput.trim()) { e.preventDefault(); moodMutation.mutate(moodInput.trim()); }
                    if (e.key === 'Escape') { setMoodEditing(false); setMoodInput(''); }
                  }}
                  inputProps={{ maxLength: 200 }}
                  autoFocus={moodEditing}
                  fullWidth
                  InputProps={{
                    startAdornment: <MoodIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)', mr: 1 }} />,
                    endAdornment: moodInput.trim() ? (
                      <Button
                        size="small"
                        onClick={() => moodMutation.mutate(moodInput.trim())}
                        disabled={moodMutation.isPending}
                        sx={{ textTransform: 'none', color: B.green, fontWeight: 700, fontSize: 12, minWidth: 0, px: 1 }}
                      >
                        <SendIcon sx={{ fontSize: 16 }} />
                      </Button>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '18px 18px 18px 4px',
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      fontSize: 14,
                      height: 40,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                    },
                    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
        <Stack spacing={1} sx={{ flex: 1, justifyContent: 'flex-start', minWidth: 0 }}>
          {insights
            .filter((ins) => ins.type === 'success' || !dismissedInsights.texts.includes(ins.text))
            .slice(0, 4)
            .map((ins, i) => {
              const s = insightStyles[ins.type] || insightStyles.info;
              const canDismiss = ins.type !== 'success';
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 3, bgcolor: s.bg, border: `1px solid ${s.border}` }}>
                  <Box sx={{ color: s.color, display: 'flex', flexShrink: 0 }}>{s.icon}</Box>
                  <Typography variant="body2" sx={{ color: s.color, fontWeight: 500, fontSize: 13, flex: 1 }}>{ins.text}</Typography>
                  {canDismiss && (
                    <IconButton
                      size="small"
                      onClick={() => dismissInsight(ins.text)}
                      sx={{ color: s.color, opacity: 0.5, p: 0.25, flexShrink: 0, '&:hover': { opacity: 1 } }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              );
            })}
        </Stack>
      </Box>

      {/* Row 2: Upcoming lessons + Today stats — side by side */}
      <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Upcoming lessons — left, bigger */}
        <Card sx={{ ...cardSx, flex: { md: '1 1 60%' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ py: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: B.primary }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark }}>Найближчі заняття</Typography>
            </Stack>
            {upcoming.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає запланованих на найближчі 24 год</Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {upcoming.map((e) => (
                  <Box
                    key={e.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2.5,
                      bgcolor: '#F8F7FF',
                      border: '1px solid #EDE9FE',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: '#EDE9FE' },
                    }}
                  >
                    <Box sx={{ width: 46, height: 38, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight={800} sx={{ color: B.primary, fontSize: 13 }}>
                        {dayjs(e.startTime).format('HH:mm')}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }} noWrap>{e.studentName}</Typography>
                      <Typography variant="caption" sx={{ color: '#8891A5' }}>
                        {dayjs(e.startTime).format('YYYY-MM-DD') !== todayStr ? 'Завтра, ' : ''}
                        {dayjs(e.startTime).format('HH:mm')} — {dayjs(e.endTime).format('HH:mm')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Today stats — right, 2x2 grid + учнів */}
        <Card sx={{ ...cardSx, flex: { md: '0 0 auto' }, minWidth: 0 }}>
          <CardContent sx={{ py: 2.5, px: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark, mb: 1.5 }}>Сьогодні</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {[
                { label: 'Всього', value: todayTotal, icon: <PendingActionsIcon sx={{ fontSize: 20 }} />, bg: '#EDE9FE', color: B.primary },
                { label: 'Проведено', value: todayCompleted, icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />, bg: '#D1FAE5', color: '#059669' },
                { label: 'Перенесено', value: todayRescheduled, icon: <SwapHorizIcon sx={{ fontSize: 20 }} />, bg: '#FEF3C7', color: '#D97706' },
                { label: 'Скасовано', value: todayCancelled, icon: <EventBusyIcon sx={{ fontSize: 20 }} />, bg: '#FEE2E2', color: '#EF4444' },
              ].map((s) => (
                <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 2.5, bgcolor: s.bg }}>
                  <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: B.dark, lineHeight: 1 }}>{s.value}</Typography>
                    <Typography variant="caption" sx={{ color: '#8891A5', fontWeight: 500 }}>{s.label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, px: 1.5, py: 1, borderRadius: 2.5, bgcolor: '#FFE4D6' }}>
              <PersonIcon sx={{ fontSize: 20, color: B.orange }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: B.dark, lineHeight: 1 }}>
                {new Set(todayEvents.map((e) => e.studentId)).size}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8891A5', fontWeight: 500 }}>учнів</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Row 3: Journal reminders */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AssignmentIcon sx={{ color: B.primary }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              Журнали учнів
            </Typography>
          </Stack>

          {allJournalsDone ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 2,
                borderRadius: 3,
                bgcolor: '#F0FDF4',
                border: '1px solid #BBF7D0',
              }}
            >
              <EmojiEventsIcon sx={{ color: '#22C55E', fontSize: 32 }} />
              <Typography variant="body1" sx={{ color: '#166534', fontWeight: 600 }}>
                {doneMessage}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {journalReminders.slice(0, 5).map((r) => (
                <Box
                  key={r.studentId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.2,
                    borderRadius: 2.5,
                    bgcolor: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FEF3C7' },
                  }}
                  onClick={() => navigate(`/tutor/students/${r.studentId}`)}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AssignmentIcon sx={{ fontSize: 18, color: '#D97706' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>
                        {r.studentName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#92400E' }}>
                        {r.count === 1 ? '1 незаповнений урок' : `${r.count} незаповнених уроків`} — заповни журнал
                      </Typography>
                    </Box>
                  </Stack>
                  <ArrowForwardIcon sx={{ color: '#D97706', fontSize: 18 }} />
                </Box>
              ))}
              {journalReminders.length > 5 && (
                <Typography variant="caption" sx={{ color: '#8891A5', pl: 1 }}>
                  ...та ще {journalReminders.length - 5}
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Row 4: Chart (full width) */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: B.dark, mb: 2 }}>Активність за тиждень</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 13, fill: '#8891A5' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8891A5' }} axisLine={false} tickLine={false} width={28} />
              <RechartsTooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="Проведено" fill="#22C55E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Заплановано" fill={B.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Перенесено" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Скасовано" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Row 6: Week progress + Summary */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 3 }}>
          <Grid container spacing={0}>
            {/* Week progress */}
            <Grid item xs={12} md={6} sx={{ px: { xs: 0, md: 3 }, py: { xs: 2, md: 0 }, borderRight: { md: '1px solid #EDE9FE' } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark, mb: 1 }}>Прогрес тижня</Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="h3" fontWeight={800} sx={{ color: B.primary }}>{weekCompleted}</Typography>
                <Typography variant="body1" sx={{ color: '#8891A5' }}>/ {weekRelevant} занять</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={weekProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: '#EDE9FE',
                  '& .MuiLinearProgress-bar': { borderRadius: 5, background: `linear-gradient(90deg, ${B.primary}, #22C55E)` },
                }}
              />
              <Typography variant="caption" sx={{ color: '#8891A5', mt: 0.5, display: 'block' }}>{weekProgress}% виконано</Typography>
            </Grid>

            {/* Week summary */}
            <Grid item xs={12} md={6} sx={{ px: { xs: 0, md: 3 }, py: { xs: 2, md: 0 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark, mb: 1.5 }}>Підсумок тижня</Typography>
              <Stack spacing={1.5}>
                {[
                  { label: 'Проведено', value: weekCompleted, color: '#22C55E' },
                  { label: 'Заплановано', value: weekScheduled, color: B.primary },
                  { label: 'Перенесено', value: weekRescheduled, color: '#F59E0B' },
                  { label: 'Скасовано', value: weekCancelled, color: '#EF4444' },
                ].map((r) => (
                  <Stack key={r.label} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                      <Typography variant="body2" sx={{ color: '#8891A5' }}>{r.label}</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{r.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

    </Stack>
  );
};

export default TutorHomePage;
